/** One rule from StaffMatchingService.match_staff */
export interface StaffMatchRule {
  rule_id?: string;
  rule_name?: string;
  passed?: boolean;
  score_contribution?: number;
  weight?: number;
  explanation?: string;
  message?: string;
}

export interface StaffMatchSummaryInput {
  match_score?: number;
  matched_rules?: StaffMatchRule[];
  failed_required_rules?: string[];
  explanation?: string;
}

function stripEmojis(text: string): string {
  return text.replace(/[\u{1F300}-\u{1FAFF}\u2600-\u27BF]/gu, '').trim();
}

/**
 * Short, human-readable summary of what drove the match score (for UI subtitles).
 */
export function formatStaffMatchSummary(
  input: StaffMatchSummaryInput | null | undefined,
  maxLen = 160,
): string {
  if (!input) return '';

  const rules = input.matched_rules ?? [];
  if (rules.length > 0) {
    const parts = rules
      .filter((r) => r.rule_name)
      .slice(0, 5)
      .map((r) => {
        const status = r.passed ? 'met' : 'missed';
        const contrib =
          typeof r.score_contribution === 'number'
            ? ` ${Math.round(r.score_contribution)}%`
            : '';
        return `${r.rule_name}: ${status}${contrib}`;
      });
    if (parts.length) {
      const score =
        typeof input.match_score === 'number'
          ? ` → ${input.match_score <= 1 ? (input.match_score * 100).toFixed(0) : input.match_score.toFixed(0)}% overall`
          : '';
      const text = parts.join(' · ') + score;
      return text.length <= maxLen ? text : `${text.slice(0, maxLen - 1)}…`;
    }
  }

  if (input.failed_required_rules?.length) {
    const text = `Missing required: ${input.failed_required_rules.join(', ')}`;
    return text.length <= maxLen ? text : `${text.slice(0, maxLen - 1)}…`;
  }

  if (input.explanation) {
    const line = stripEmojis(input.explanation.split('\n').find((l) => l.trim()) ?? '');
    if (line) {
      return line.length <= maxLen ? line : `${line.slice(0, maxLen - 1)}…`;
    }
  }

  if (typeof input.match_score === 'number') {
    const pct = input.match_score <= 1 ? input.match_score * 100 : input.match_score;
    return `${pct.toFixed(0)}% overall match`;
  }

  return '';
}
